/**
 * Rate limit notice
 * Shows a single toast per rate-limit episode instead of one per rejected call.
 */

import { toast } from '@/components/ui/Toast';

const RATE_LIMIT_TOAST_ID = 'rate-limit';
const MIN_NOTICE_MS = 5000;

// Until this timestamp further rate-limit notices are suppressed
let suppressUntil = 0;

/**
 * Notify the user that requests are being rate limited.
 *
 * Calls made while a notice is active are ignored, so a burst of 429
 * responses produces exactly one toast that stays until the limit resets.
 *
 * @param resetAtMs - Epoch milliseconds when requests are allowed again
 */
export function notifyRateLimited(resetAtMs: number): void {
  const now = Date.now();
  if (now < suppressUntil) {
    return;
  }

  const durationMs = Math.max(resetAtMs - now, MIN_NOTICE_MS);
  suppressUntil = now + durationMs;

  const resetTime = new Date(suppressUntil).toLocaleTimeString();
  toast.warning(
    `Please wait - server capacity is limited. Try again at ${resetTime}.`,
    { id: RATE_LIMIT_TOAST_ID, duration: durationMs }
  );
}

/**
 * Notify from an API error's 429 payload (`details.retry_after` in seconds).
 */
export function notifyRateLimitedFromError(details?: Record<string, unknown>): void {
  const retryAfter = Number(details?.['retry_after']);
  const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 60;
  notifyRateLimited(Date.now() + seconds * 1000);
}
